import debounce from "debounce";
import FindAProviderStoryBook from "./find-a-provider.init.storybook";
import ApiClientCommon from "@lib/apiClientCommon";

class ApiClient {
  constructor() {
    this.#common = new ApiClientCommon("/umbraco/surface/findaprovider");
  }

  /** @type ApiClientCommon */
  #common;

  /**
   * @param {FormParams} searchParams
   * @return {Promise<string|boolean>}
   */
  async loadSearchResults(searchParams) {
    return await this.#common.doFormPost({
      name: "loadSearchResults",
      path: "/ResultsFor",
      formParams: searchParams,
    });
  }
  /**
   * @param {string} state
   * @param {string} token
   * @return {Promise<string|boolean>}
   */
  async loadStateResults(state, token) {
    return await this.#common.doFormPost({
      name: "loadStateResults",
      path: `/ResultsForState`,
      formParams: { state, "__RequestVerificationToken": token }
    });
  }
}

class UI {
  #ui;
  /** @param {HTMLDivElement} el */
  constructor(el) {
    this.#ui = {
      /** @type HTMLBodyElement */
      body: window.document.querySelector("body"),
      /** @type HTMLDivElement */
      filters: el.querySelector("div.find-a-provider__filters"),
      /** @type HTMLDetailsElement */
      details: el.querySelector("details.find-a-provider__details"),
      /** @type HTMLSpanElement */
      detailsSpan: el.querySelector(".find-a-provider__details .summary span"),
      /** @type HTMLFormElement */
      form: el.querySelector("form"),
      /** @type string */
      verificationToken: el.querySelector("input[name=__RequestVerificationToken]")?.value || "",
      /** @type HTMLButtonElement */
      submitButton: el.querySelector("button[type=submit]"),
      /** @type HTMLSelectElement */
      specialtySelect: el.querySelector("select#SpecialtyId"),
      /** @type HTMLSelectElement */
      subSpecialtySelect: el.querySelector("select#SubSpecialtyId"),
      /** @type HTMLInputElement */
      allSpecialtyItemsValue: el.querySelector("input#AllSpecialtyItemsValue"),
      /** @type HTMLDivElement */
      content: el.querySelector("div.find-a-provider__content"),
    };
    this.#addEvents();
  }

  #pagewidthThreshold = 1024;
  #onResize() {
    if (this.#ui.body.offsetWidth >= this.#pagewidthThreshold) {
      this.#ui.details.setAttribute('open', 'true');
    } else {
      this.#ui.details.removeAttribute('open');
    }
  }

  #onReset() {
    [...this.#ui.filters.querySelectorAll("input")].forEach(input => {
      if (input.id === "affiliation-0") {
        input.setAttribute("checked", "");
      } else if (input.type === "radio" || input.type === "checkbox") {
        input.removeAttribute("checked");
      } else if (input.type !== "hidden") {
        input.value = "";
        input.removeAttribute("value");
      }
    });
    [...this.#ui.filters.querySelectorAll("select")].forEach(select => {
      [...select.selectedOptions].forEach(o => o.removeAttribute("selected"));
    });
  }

  /**
   * @param {HTMLSelectElement} target
   * @param {HTMLSelectElement?} otherSelect
   */
  #addOnPageSizeChange(target, otherSelect) {
    target.addEventListener("change", () => {
      otherSelect?.setAttribute("disabled", "");
      this.#ui.submitButton.click();
    })
  }

  #connectPageEvents() {
    const pageSize1 = this.#content.querySelector("select#page-size");
    const pageSize2 = this.#content.querySelector("select#page-size-2");
    if (pageSize1) this.#addOnPageSizeChange(pageSize1, pageSize2);
    if (pageSize2) this.#addOnPageSizeChange(pageSize2, pageSize1);
  }

  #addEvents() {
    window.onresize = debounce(() => {
      this.#onResize()
    }, 50);
    this.#ui.details.addEventListener("toggle", () => {
    });
    this.#onResize();
    this.#connectPageEvents();
    this.#ui.details.querySelector("button[type=reset]")?.addEventListener("click", () => {
      this.#onReset();
    })
  }

  get specialtySelect() { return this.#ui.specialtySelect; }
  get allSpecialtyItemsInput() { return this.#ui.allSpecialtyItemsValue; }

  /** @param {[SpecialtyItem]?} items */
  updateSubSpecialtyItems(items) {
    const firstItemValue = this.#ui.subSpecialtySelect[0].text;
    this.#ui.subSpecialtySelect.innerHTML = `<option value="">${firstItemValue}</option>${
      items?.map(i => `<option value="${i.value}">${i.text}</option>`)?.join("")
    }`;
    if (items?.length > 0) {
      this.#ui.subSpecialtySelect.parentElement.classList.remove("is-hidden");
      this.#ui.subSpecialtySelect.removeAttribute("disabled");
    } else {
      this.#ui.subSpecialtySelect.parentElement.classList.add("is-hidden");
      this.#ui.subSpecialtySelect.setAttribute("disabled", "");
    }
  }

  get submitButton() { return this.#ui.submitButton; }

  /** @return HTMLDivElement */
  get #content() { return this.#ui.content; }

  /** @return FormParams */
  getSearchParams() {
    const form = this.#ui.form;
    const searchParams = {};

    [...form.elements].forEach(e => {
      if (e.disabled || !e.name || e.matches("button") || e.tagName === "FIELDSET") return;
      if (e.type === "radio") {
        searchParams[e.name] = form.elements[e.name].value;
      } else if (e.type === "checkbox") {
        searchParams[e.name] = `${e.checked}`;
      } else {
        const { value } = e;
        if (value === "false" && searchParams[e.name]) return;
        searchParams[e.name] = value || "";
      }
    });

    return searchParams;
  }

  #updateQueryState() {
    const state = this.#content.querySelector("div.card-deck--physician")?.dataset?.state;
    const url = new URL(window.location)
    url.searchParams.set("q", state || "")
    window.history.pushState(null, '', url);
  }

  /** @param {string} results */
  updateResults(results) {
    this.#content.innerHTML = results;
    this.#connectPageEvents();
    this.#updateQueryState();
  }

  /** @return [HTMLAnchorElement] */
  getPageLinks() { return [...this.#content.querySelectorAll("a.pagination__item")]; }
  get verificationToken() { return this.#ui.verificationToken; }
}

/**
 * @typedef SpecialtyItem
 * @property text
 * @property value
 */
/**
 * @param {UI} ui
 * @return {Object.<string, [SpecialtyItem]>}
 */
function getGroupSpecialtiesMap(ui) {
  try {
    const value = ui.allSpecialtyItemsInput.value;
    return JSON.parse(window.atob(value));
  } catch (err) {
    return {}
  }
}

/** @param {HTMLDivElement} el */
function FindAProvider(el) {
  const api = new ApiClient();
  const ui = new UI(el);

  const specialtyMap = getGroupSpecialtiesMap(ui);

  ui.specialtySelect.addEventListener("change", () => {
    const subItems = specialtyMap[ui.specialtySelect.value || ""];
    ui.updateSubSpecialtyItems(subItems);
  });

  const addPageLinkHandlers = () => ui.getPageLinks().forEach(a => {
    const href = a.getAttribute("href");
    const state = href?.length > 3 ? href.substring(3) : "";
    a.addEventListener("click", async e => {
      e.preventDefault();
      if (!state) return;
      const results = await api.loadStateResults(state, ui.verificationToken);
      if (results) {
        ui.updateResults(results);
        addPageLinkHandlers();
      }
    })
  });
  addPageLinkHandlers();

  ui.submitButton.addEventListener("click", async e => {
    e.preventDefault();
    const searchParams = ui.getSearchParams();
    const results = await api.loadSearchResults(searchParams);
    if (results) {
      ui.updateResults(results);
      addPageLinkHandlers();
    }
  }, { capture: true });
}

/** @param {HTMLDivElement} el */
export default function conditionalInit(el) {
  if (window.isTurnStorybook)
    FindAProviderStoryBook(el)
  else
    FindAProvider(el);
}
